import { useMemo, useState } from "react";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCan } from "@/lib/auth/useCan";

import { useRoster, type CompanyOption, type RequestAction, type RosterParent } from "../api";
import { RelationshipDialog } from "./RelationshipDialog";

/**
 * Quick view: the company's connected companies as a flat list with
 * relationship chips (its parents above, its children below), pending-change
 * and AI chips, and propose-change shortcuts. The full tree + workflow live
 * in the expanded view.
 */
export function CompanyQuickInfo({ companyId }: { companyId: string }) {
  const roster = useRoster();
  const canWrite = useCan("parent_companies.write");
  const [dialog, setDialog] = useState<{
    mode: RequestAction;
    parent?: CompanyOption | null;
    child?: CompanyOption | null;
  } | null>(null);

  const parent = useMemo<RosterParent | undefined>(
    () => roster.data?.parents.find((p) => p.id === companyId),
    [roster.data, companyId],
  );

  if (roster.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  }
  if (!parent) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        This company has no children in Cinch (it may have been unlinked since
        the grid loaded).
      </p>
    );
  }

  const asOption = (id: string, name: string): CompanyOption => ({
    id,
    name,
    is_active: null,
    account_name: null,
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold">{parent.name}</h3>
          {parent.is_active === false && <StatusBadge value="inactive" />}
          {parent.ai_status && <StatusBadge value={parent.ai_status} />}
          {parent.pending_count > 0 && (
            <StatusBadge value="pending" label={`${parent.pending_count} pending`} />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {[parent.brand_name, parent.account_name, [parent.city, parent.state].filter(Boolean).join(", ")]
            .filter(Boolean)
            .join(" · ") || "No brand/account details visible"}
        </p>
      </div>

      <Separator />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-semibold">Connected companies</h4>

        {parent.parent_ids.map((pid, i) => (
          <ConnectedRow
            key={pid}
            name={parent.parent_names[i] ?? pid}
            chip={<StatusBadge value="parent" label="Parent of this company" />}
          />
        ))}

        {parent.children.map((c) => (
          <ConnectedRow
            key={c.id}
            name={c.name}
            muted={c.is_active === false}
            chip={
              <>
                <StatusBadge
                  value="child"
                  label={c.child_count > 0 ? "Child · has children" : "Child"}
                />
                {c.is_active === false && <StatusBadge value="inactive" />}
              </>
            }
            actions={
              canWrite ? (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground"
                    onClick={() =>
                      setDialog({
                        mode: "move",
                        parent: asOption(parent.id, parent.name),
                        child: asOption(c.id, c.name),
                      })
                    }
                  >
                    Move
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground"
                    onClick={() =>
                      setDialog({
                        mode: "remove",
                        parent: asOption(parent.id, parent.name),
                        child: asOption(c.id, c.name),
                      })
                    }
                  >
                    Unlink
                  </Button>
                </div>
              ) : undefined
            }
          />
        ))}
      </div>

      {canWrite && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setDialog({ mode: "add", parent: asOption(parent.id, parent.name) })
          }
        >
          Propose adding a child…
        </Button>
      )}

      <p className="text-xs text-muted-foreground">
        Changes here are proposals — a second user approves before anything
        lands in Cinch. Open the full view for the relationship tree, approvals,
        change log, and AI review.
      </p>

      {dialog && (
        <RelationshipDialog
          open
          onOpenChange={(v) => {
            if (!v) setDialog(null);
          }}
          mode={dialog.mode}
          parent={dialog.parent}
          child={dialog.child}
        />
      )}
    </div>
  );
}

function ConnectedRow({
  name,
  chip,
  actions,
  muted = false,
}: {
  name: string;
  chip: React.ReactNode;
  actions?: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
      <span className={`min-w-0 flex-1 truncate text-sm font-medium ${muted ? "opacity-60" : ""}`}>
        {name}
      </span>
      <div className="flex shrink-0 items-center gap-1">{chip}</div>
      {actions}
    </div>
  );
}
